import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Lock, X } from "lucide-react";

// Step-up re-authentication prompt for sensitive actions (HIPAA §164.312(d)).
export default function ReauthModal({ open, onClose, onSuccess, title = "Confirm it's you" }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reauth", { password });
      setPassword("");
      setLoading(false);
      onSuccess();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reauth-modal">
      <div className="bg-card border border-border rounded-lg w-full max-w-sm p-6 shadow-xl">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600" />
            <h3 className="font-heading text-lg font-semibold">{title}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="reauth-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">For your security, please re-enter your password to continue.</p>
        <form onSubmit={submit} className="space-y-3">
          <input
            data-testid="reauth-password-input"
            type="password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            placeholder="Your password"
          />
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reauth-error">{error}</p>}
          <button
            data-testid="reauth-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm
          </button>
        </form>
      </div>
    </div>
  );
}
